import {useState} from 'react';
import {useQueryClient} from '@tanstack/react-query';
import {formatDistanceToNow} from 'date-fns';
import {UploadCloud, AlertCircle, TriangleAlert, FileSpreadsheet, RefreshCw} from 'lucide-react';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Badge,
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
  Input,
  NativeSelect,
  NativeSelectOption,
  Spinner
} from '@kroll-resolver-crisp/ui-components-react';
import {
  type ColumnMapping,
  type CustomerFeedbackChange,
  type CustomerFeedbackUpload,
  submissionsApi
} from '@/api/submissions';
import {getApiErrorMessage} from '@/api/client';
import {
  type CustomerFeedbackSource,
  customerFeedbackSourcesApi,
  customerFeedbackSourcesQueryKey,
  useCustomerFeedbackSources
} from '@/api/customerFeedbackSources';
import {showToast} from '@/lib/toast';
import {customerDecisionLabel, customerLiveStatusLabel} from '@/lib/customerFeedback';
import {PLATFORM_CODENAME, PLATFORM_ID} from '@/lib/platform';
import {toneBadge} from '@/lib/status';
import {cn} from '@/lib/utils';
import {Table, TableBody, TableCell, TableHead, TableHeader, TableRow} from '@/components/ui/table';
import {TablePanel} from '@/components/ui/table-panel';
import {TruncatedText} from '@/components/shared/TruncatedText';

// What this deployment's customer sends - Falcon's Bad Actor sheet (Pinterest) or Pioneer's
// per-video file (YouTube). The API tells the two apart by their headers either way.
const FILE_SHAPE =
  PLATFORM_ID === 'pinterest'
    ? {
        matchedBy: 'Pinterest user ID (or username when the ID is missing)',
        columns:
          'the Bad Actor sheet, with ACTION TAKEN, USER ID, NOTES and # of Pins enqueued for action',
        repeats: 'Where a user appears more than once, the latest date submitted wins.'
      }
    : {
        matchedBy: 'video ID and vertical',
        columns: 'External Video Id, Flag Vertical, Flag Day, Last Decision, Live Status',
        repeats: 'Where a video and vertical appear more than once, the latest Flag Day wins.'
      };

// Matches the API's request limit.
const MAX_FILE_SIZE = 64 * 1024 * 1024;

type Phase = 'select' | 'previewing' | 'preview' | 'applying';

// Where the feedback being previewed came from: an uploaded CSV, or a Google Sheet PRI fetched.
type Origin = {kind: 'file'} | {kind: 'sheet'; source: CustomerFeedbackSource};

// The client's feedback, from a Google Sheet PRI fetches itself or an uploaded CSV. Preview first (the
// server matches it and writes nothing), then confirm - the confirm carries the preview's hash, so the
// server refuses to write anything other than what was reviewed here.
export function CustomerFeedbackUploadDialog({
  onClose,
  onApplied
}: {
  readonly onClose: () => void;
  readonly onApplied: (result: CustomerFeedbackUpload) => void;
}) {
  const queryClient = useQueryClient();
  const sources = useCustomerFeedbackSources(true);
  const [phase, setPhase] = useState<Phase>('select');
  const [origin, setOrigin] = useState<Origin>({kind: 'file'});
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<CustomerFeedbackUpload | null>(null);
  // Columns the uploader pointed at by hand; anything absent is found by its usual title.
  const [mapping, setMapping] = useState<ColumnMapping>({});
  const [remapping, setRemapping] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handlePreview() {
    if (!file) {
      return;
    }

    if (file.size > MAX_FILE_SIZE) {
      setError('The file is over the 64 MB limit.');
      return;
    }

    setError(null);
    setPhase('previewing');
    try {
      setPreview(await submissionsApi.previewCustomerFeedback(file));
      setMapping({});
      setOrigin({kind: 'file'});
      setPhase('preview');
    } catch (error_) {
      setError(getApiErrorMessage(error_, 'Preview failed'));
      setPhase('select');
    }
  }

  async function handleReviewSheet(source: CustomerFeedbackSource) {
    setError(null);
    setPhase('previewing');
    try {
      const pending = await customerFeedbackSourcesApi.pending(source.id);
      if (pending) {
        setPreview(pending);
        setOrigin({kind: 'sheet', source});
        setPhase('preview');
      } else {
        setError('Nothing new in this sheet since it was last confirmed.');
        setPhase('select');
      }
    } catch (error_) {
      setError(getApiErrorMessage(error_, 'Preview failed'));
      setPhase('select');
    }
  }

  // The sheet's mapping is saved on the source (every later fetch is read the same way), then
  // the pending rows are re-previewed with it.
  async function handleSheetMapping(source: CustomerFeedbackSource, next: ColumnMapping) {
    setError(null);
    setRemapping(true);
    try {
      await customerFeedbackSourcesApi.setColumnMapping(source.id, next);
      const pending = await customerFeedbackSourcesApi.pending(source.id);
      if (pending) {
        setPreview(pending);
      }
    } catch (error_) {
      setError(getApiErrorMessage(error_, 'Could not apply the column mapping'));
    }

    setRemapping(false);
  }

  // Re-previews the uploaded file with other columns chosen; stays on the preview if that fails.
  async function handleFileMapping(next: ColumnMapping) {
    if (!file) {
      return;
    }

    setError(null);
    setRemapping(true);
    try {
      setPreview(await submissionsApi.previewCustomerFeedback(file, next));
      setMapping(next);
    } catch (error_) {
      setError(getApiErrorMessage(error_, 'Could not apply the column mapping'));
    }

    setRemapping(false);
  }

  async function handleSync(source: CustomerFeedbackSource) {
    try {
      await customerFeedbackSourcesApi.sync(source.id);
      await queryClient.invalidateQueries({queryKey: customerFeedbackSourcesQueryKey});
      showToast(`Fetching ${source.name} - this takes up to a minute`);
    } catch (error_) {
      setError(getApiErrorMessage(error_, 'Sync failed'));
    }
  }

  async function handleApply() {
    if (!preview || (origin.kind === 'file' && !file)) {
      return;
    }

    setError(null);
    setPhase('applying');
    try {
      onApplied(
        origin.kind === 'sheet'
          ? await customerFeedbackSourcesApi.apply(origin.source.id, preview.file_sha256)
          : await submissionsApi.applyCustomerFeedback(file!, preview.file_sha256, mapping)
      );
      await queryClient.invalidateQueries({queryKey: customerFeedbackSourcesQueryKey});
    } catch (error_) {
      setError(getApiErrorMessage(error_, 'Upload failed'));
      setPhase('preview');
    }
  }

  const busy = phase === 'previewing' || phase === 'applying' || remapping;
  const writeCount = preview ? preview.new + preview.changed : 0;

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open && !busy) {
          onClose();
        }
      }}>
      <DialogContent className="sm:max-w-4xl">
        <DialogHeader>
          <DialogTitle>Customer feedback</DialogTitle>
          <DialogDescription>
            Matches {PLATFORM_CODENAME}&rsquo;s feedback file to its submissions by{' '}
            {FILE_SHAPE.matchedBy}. Nothing is saved until you review the changes and confirm.
          </DialogDescription>
        </DialogHeader>

        <DialogBody className="min-w-0 gap-4">
          {phase === 'select' || phase === 'previewing' ? (
            <>
              <SheetSources
                sources={(sources.data?.sources ?? []).filter((s) => s.enabled)}
                busy={busy}
                onReview={(source) => void handleReviewSheet(source)}
                onSync={(source) => void handleSync(source)}
              />
              <p className="text-muted-foreground text-xs leading-relaxed">
                Or upload a CSV export of{' '}
                <strong className="text-foreground font-medium">{FILE_SHAPE.columns}</strong>.{' '}
                {FILE_SHAPE.repeats}
              </p>
              <Empty variant="outline">
                <EmptyHeader>
                  <EmptyMedia variant="icon">
                    <UploadCloud />
                  </EmptyMedia>
                  <EmptyTitle>{file ? file.name : 'Choose CSV file'}</EmptyTitle>
                  <EmptyDescription>Up to 64 MB.</EmptyDescription>
                </EmptyHeader>
                <EmptyContent>
                  <Input
                    type="file"
                    accept=".csv"
                    aria-label="Choose CSV file"
                    disabled={busy}
                    onChange={(e) => {
                      setFile(e.target.files?.[0] ?? null);
                      setError(null);
                    }}
                  />
                </EmptyContent>
              </Empty>
            </>
          ) : null}

          {preview && (phase === 'preview' || phase === 'applying') ? (
            <ColumnMapper
              preview={preview}
              disabled={busy}
              onChange={(next) =>
                void (origin.kind === 'sheet'
                  ? handleSheetMapping(origin.source, next)
                  : handleFileMapping(next))
              }
            />
          ) : null}

          {preview && (phase === 'preview' || phase === 'applying') ? (
            <FeedbackPreview
              preview={preview}
              fileName={
                origin.kind === 'sheet' ? `Google Sheet: ${origin.source.name}` : (file?.name ?? '')
              }
            />
          ) : null}

          {error ? (
            <Alert variant="destructive">
              <AlertCircle />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          ) : null}
        </DialogBody>

        <DialogFooter sticky>
          <Button type="button" variant="ghost" disabled={busy} onClick={onClose}>
            Cancel
          </Button>
          {phase === 'select' || phase === 'previewing' ? (
            <Button type="button" disabled={busy || !file} onClick={() => void handlePreview()}>
              {phase === 'previewing' ? <Spinner /> : null}
              {phase === 'previewing' ? 'Checking…' : 'Preview upload'}
            </Button>
          ) : (
            <>
              <Button
                type="button"
                variant="secondary"
                disabled={busy}
                onClick={() => {
                  setPreview(null);
                  setPhase('select');
                }}>
                Back
              </Button>
              <Button
                type="button"
                disabled={busy || writeCount === 0}
                onClick={() => void handleApply()}>
                {phase === 'applying' ? <Spinner /> : null}
                {writeCount === 0
                  ? 'Nothing to update'
                  : `Confirm ${writeCount.toLocaleString()} update${writeCount === 1 ? '' : 's'}`}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Which column of the uploaded file each value is read from. PRI guesses from the column titles; if
// the customer's sheet words one differently, pick the right column here and the preview re-runs.
function ColumnMapper({
  preview,
  disabled,
  onChange
}: {
  readonly preview: CustomerFeedbackUpload;
  readonly disabled: boolean;
  readonly onChange: (mapping: ColumnMapping) => void;
}) {
  return (
    <details className="rounded-md border p-3" open={preview.fields.some((f) => !f.column)}>
      <summary className="cursor-pointer text-xs font-medium">Column mapping</summary>
      <div className="mt-3 grid gap-2 sm:grid-cols-2">
        {preview.fields.map((field) => (
          <label key={field.key} className="flex min-w-0 flex-col gap-1 text-xs">
            <span className="text-muted-foreground">
              {field.label}
              {field.required ? ' *' : ''}
            </span>
            <NativeSelect
              wrapperClassName="min-w-0"
              disabled={disabled}
              value={field.column ?? ''}
              onChange={(e) => {
                // Every field's current column, with this one changed - what the server saves/applies.
                onChange({
                  ...Object.fromEntries(preview.fields.map((f) => [f.key, f.column ?? ''])),
                  [field.key]: e.target.value
                });
              }}>
              <NativeSelectOption value="">Not in file</NativeSelectOption>
              {preview.columns.map((column) => (
                <NativeSelectOption key={column} value={column}>
                  {column}
                </NativeSelectOption>
              ))}
            </NativeSelect>
          </label>
        ))}
      </div>
    </details>
  );
}

// The sheets PRI fetches for this customer (Settings > Customer feedback sheets): when each was last
// read, whether it has changes no one has confirmed, and a way to fetch it again now.
function SheetSources({
  sources,
  busy,
  onReview,
  onSync
}: {
  readonly sources: CustomerFeedbackSource[];
  readonly busy: boolean;
  readonly onReview: (source: CustomerFeedbackSource) => void;
  readonly onSync: (source: CustomerFeedbackSource) => void;
}) {
  if (sources.length === 0) {
    return null;
  }

  return (
    <div className="flex flex-col gap-2">
      {sources.map((source) => (
        <div key={source.id} className="flex items-center gap-3 rounded-md border p-3">
          <FileSpreadsheet className="text-muted-foreground size-4 shrink-0" />
          <div className="min-w-0 flex-1">
            <p className="text-xs font-medium">
              {source.name}{' '}
              {source.has_pending ? <Badge {...toneBadge('info')}>Changes to review</Badge> : null}
            </p>
            <p className="text-muted-foreground text-xs">
              {source.fetch_requested_at
                ? 'Fetching…'
                : source.last_fetched_at
                  ? `Last read ${formatDistanceToNow(new Date(source.last_fetched_at), {addSuffix: true})}`
                  : 'Not read yet'}
            </p>
            {source.last_fetch_error ? (
              <p className="text-destructive-ink text-xs">{source.last_fetch_error}</p>
            ) : null}
          </div>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={busy || source.fetch_requested_at !== null}
            onClick={() => {
              onSync(source);
            }}>
            <RefreshCw />
            Sync now
          </Button>
          <Button
            type="button"
            size="sm"
            disabled={busy || !source.has_pending}
            onClick={() => {
              onReview(source);
            }}>
            Review changes
          </Button>
        </div>
      ))}
    </div>
  );
}

export function FeedbackPreview({
  preview,
  fileName
}: {
  readonly preview: CustomerFeedbackUpload;
  readonly fileName: string;
}) {
  const shown = preview.changes.length;
  const total = preview.new + preview.changed;
  // Only the columns this customer's file actually carries.
  const columns = {
    liveStatus: preview.changes.some((c) => c.new_live_status ?? c.old_live_status),
    notes: preview.changes.some((c) => c.new_notes ?? c.old_notes),
    pins: preview.changes.some((c) => c.new_pins_actioned !== null || c.old_pins_actioned !== null)
  };

  return (
    <>
      <div>
        <p className="text-foreground text-xs font-medium">{fileName}</p>
        <p className="text-muted-foreground text-xs">
          {preview.rows.toLocaleString()} rows read as {preview.format}.
        </p>
      </div>
      <div className="flex flex-wrap gap-2">
        <Badge {...toneBadge('success')}>{preview.new.toLocaleString()} new</Badge>
        <Badge {...toneBadge('warning')}>{preview.changed.toLocaleString()} changed</Badge>
        <Badge variant="secondary">{preview.unchanged.toLocaleString()} unchanged</Badge>
        <Badge variant="outline">{preview.unmatched.toLocaleString()} no matching submission</Badge>
        {preview.duplicates_collapsed > 0 ? (
          <Badge variant="outline">
            {preview.duplicates_collapsed.toLocaleString()} repeated rows (latest kept)
          </Badge>
        ) : null}
        {preview.invalid > 0 ? (
          <Badge variant="destructive">{preview.invalid.toLocaleString()} invalid rows</Badge>
        ) : null}
      </div>

      {preview.unknown_verticals.length > 0 ? (
        <Alert>
          <TriangleAlert />
          <AlertTitle>Verticals with no submissions for this customer</AlertTitle>
          <AlertDescription>
            These rows were skipped. If a name should match one of ours, it needs adding as an
            alias.
            <ul className="list-inside list-disc">
              {preview.unknown_verticals.map((v) => (
                <li key={v.vertical}>
                  {v.vertical} ({v.rows.toLocaleString()} rows)
                </li>
              ))}
            </ul>
          </AlertDescription>
        </Alert>
      ) : null}

      {shown > 0 ? (
        <TablePanel className="flex max-h-80 min-h-0 flex-col">
          <Table containerClassName="min-h-0 overflow-y-auto [&_thead_th]:bg-card [&_thead_th]:sticky [&_thead_th]:top-0 [&_thead_th]:z-10 [&_thead_th]:shadow-[inset_0_-1px_0_var(--border)]">
            <TableHeader>
              <TableRow>
                <TableHead>{PLATFORM_ID === 'pinterest' ? 'User ID' : 'Video ID'}</TableHead>
                <TableHead>Vertical</TableHead>
                <TableHead>Submission</TableHead>
                <TableHead>Decision</TableHead>
                {columns.liveStatus ? <TableHead>Platform status</TableHead> : null}
                {columns.notes ? <TableHead>Notes</TableHead> : null}
                {columns.pins ? <TableHead>Pins</TableHead> : null}
                <TableHead>Flag day</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {preview.changes.map((change) => (
                <TableRow key={`${change.submission_id}-${change.policy_violation}`}>
                  <TableCell className="font-mono text-xs">{change.external_id}</TableCell>
                  <TableCell>{change.policy_violation}</TableCell>
                  <TableCell className="max-w-[200px]">
                    <TruncatedText>{change.display_name ?? '—'}</TruncatedText>
                    {change.is_historic ? (
                      <Badge variant="outline" className="mt-1">
                        Historic
                      </Badge>
                    ) : null}
                  </TableCell>
                  <TableCell>
                    <ChangedValue
                      change={change}
                      from={customerDecisionLabel(change.old_last_decision)}
                      to={customerDecisionLabel(change.new_last_decision)}
                    />
                  </TableCell>
                  {columns.liveStatus ? (
                    <TableCell>
                      <ChangedValue
                        change={change}
                        from={customerLiveStatusLabel(change.old_live_status)}
                        to={customerLiveStatusLabel(change.new_live_status)}
                      />
                    </TableCell>
                  ) : null}
                  {columns.notes ? (
                    <TableCell className="max-w-[240px]">
                      <ChangedValue change={change} from={change.old_notes} to={change.new_notes} />
                    </TableCell>
                  ) : null}
                  {columns.pins ? (
                    <TableCell>
                      <ChangedValue
                        change={change}
                        from={change.old_pins_actioned?.toString() ?? null}
                        to={change.new_pins_actioned?.toString() ?? null}
                      />
                    </TableCell>
                  ) : null}
                  <TableCell className="whitespace-nowrap">{change.flag_day}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TablePanel>
      ) : (
        <p className="text-muted-foreground text-xs">
          Nothing to update - every matched submission already has this feedback.
        </p>
      )}
      {preview.changes_truncated ? (
        <p className="text-muted-foreground text-xs">
          Showing the first {shown.toLocaleString()} of {total.toLocaleString()} updates, changes to
          existing feedback first. Confirming applies all of them.
        </p>
      ) : null}
    </>
  );
}

// A new row shows just its value; a changed one shows old → new, highlighted where it differs.
function ChangedValue({
  change,
  from,
  to
}: {
  readonly change: CustomerFeedbackChange;
  readonly from: string | null;
  readonly to: string | null;
}) {
  if (change.is_new || from === to) {
    return <span className={cn(!to && 'text-muted-foreground')}>{to ?? '—'}</span>;
  }

  return (
    <span className="bg-warning/20 rounded-md px-1 whitespace-nowrap">
      <span className="text-muted-foreground line-through">{from ?? '—'}</span> → {to ?? '—'}
    </span>
  );
}
